import { useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTelegram } from './useTelegram'

type ShareUrlOptions = {
  url: string
  text: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useShareLink = (): {
  shareUrl: (options: ShareUrlOptions) => Promise<void>
  isSharing: boolean
} => {
  const { tg, isTelegramVersionSupported } = useTelegram()
  const { formatMessage } = useIntl()
  const [isSharing, setIsSharing] = useState(false)

  const shareUrl = useCallback(
    async (options: ShareUrlOptions) => {
      setIsSharing(true)

      try {
        // Add haptic feedback only if supported
        if (isTelegramVersionSupported(6.1)) {
          tg.HapticFeedback.impactOccurred('medium')
        }

        // Priority 1: Try Telegram native share
        if (tg.openTelegramLink) {
          const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(options.url)}&text=${encodeURIComponent(options.text)}`
          tg.openTelegramLink(telegramShareUrl)
          if (isTelegramVersionSupported(6.1)) {
            tg.HapticFeedback.notificationOccurred('success')
          }
          options.onSuccess?.()
          return
        }

        // Priority 2: Try Web Share API
        if (navigator.share) {
          await navigator.share({
            title: options.text,
            text: options.text,
            url: options.url,
          })
          if (isTelegramVersionSupported(6.1)) {
            tg.HapticFeedback.notificationOccurred('success')
          }
          options.onSuccess?.()
          return
        }

        options.onSuccess?.()
      } catch (error) {
        // User cancelled share or error occurred
        if (
          error instanceof Error &&
          error.name === 'AbortError' &&
          error.message === 'Share canceled'
        ) {
          console.log('Share cancelled by user')
        } else {
          console.error('Error sharing link:', error)

          if (isTelegramVersionSupported(6.1)) {
            tg.HapticFeedback.notificationOccurred('error')
          }

          const errorInstance =
            error instanceof Error ? error : new Error(String(error))
          options.onError?.(errorInstance)

          if (isTelegramVersionSupported(6.1)) {
            tg.showPopup({
              title: formatMessage({
                id: 'share.error.title',
                defaultMessage: 'Ошибка',
              }),
              message: `${formatMessage({
                id: 'share.error.shareFailed',
                defaultMessage: 'Не удалось поделиться ссылкой',
              })} ${options.url}`,
              buttons: [{ type: 'ok' }],
            })
          } else {
            alert(
              `${formatMessage({
                id: 'share.error.title',
                defaultMessage: 'Ошибка',
              })}: ${formatMessage({
                id: 'share.error.shareFailed',
                defaultMessage: 'Не удалось поделиться ссылкой',
              })} ${options.url}`,
            )
          }
        }
      } finally {
        setIsSharing(false)
      }
    },
    [tg, formatMessage, isTelegramVersionSupported],
  )

  return {
    isSharing,
    shareUrl,
  }
}
